import { RefObject, useEffect, useState } from 'react'

import { usePrefersReducedMotion } from './usePrefersReducedMotion'

export const ANIMATION_DURATION_MS = 300
// The fade starts a frame after `show` flips, so the fallback must land after animationend.
export const ANIMATION_FALLBACK_MS = ANIMATION_DURATION_MS + 50

/**
 * Keeps an element mounted while its exit animation runs. It unmounts on
 * `animationend` from `toAnimateRef`, after the fallback delay when that
 * event never arrives (a stylesheet disabled animations), or at once when the
 * user prefers reduced motion.
 */
export function useAnimatedRender(
  show: boolean,
  toAnimateRef: RefObject<unknown>
) {
  const [shouldRender, setRender] = useState(show)
  const prefersReducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    if (show) {
      setRender(true)
      return
    }

    if (!shouldRender) return

    const fallback = setTimeout(
      () => setRender(false),
      prefersReducedMotion ? 0 : ANIMATION_FALLBACK_MS
    )

    return () => clearTimeout(fallback)
  }, [show, shouldRender, prefersReducedMotion])

  function onAnimationEnd(e: React.AnimationEvent): void {
    if (e.target === toAnimateRef.current && !show) {
      setRender(false)
    }
  }

  return [shouldRender, onAnimationEnd] as const
}

export const defaultAnimation = `
animation: fadeOut ${ANIMATION_DURATION_MS}ms;

&.element-in {
  animation: fadeIn ${ANIMATION_DURATION_MS}ms;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
`
